import {
	getTypeDocumentIdentiteEnumString,
	TypeDocumentIdentiteEnum
} from './models/api/enums/TypeDocumentIdentiteEnum';
import { KYCQuestionType } from './enums/KYC';
import { get } from 'svelte/store';
import { t } from 'svelte-i18n';
import { FrenchCounty } from './models/api/enums/FrenchCounty';
import {
	getNiveauEducationKYCString,
	NiveauEducationKYCEnum
} from './models/api/enums/NiveauEducationKYCEnum';
import { ActivitySectorPM, ActivitySectorPP } from './models/api/enums/ActivitySectorEnum';
import { docIsInvalid, isUserBeneficiary } from './utils/kyc';
import { acceptedFormatsForUserDocument } from './utils/global';
import { formatDateWithLocale, getSubyears } from '@wiseed/wikit';
import type { AccountModel } from './models/api/AccountModel';
import type { PhysicalPersonPartner } from './models/api/PhysicalPersonPartner';
import type { KYCQuestionProps } from './models/api/KYCModel';
import { LegalFormEnumFR } from './models/api/enums/LegalFormEnum';
import { FunctionOfTheLegalRepresentativeEnumFR } from './models/api/enums/FunctionOfTheLegalRepresentativeEnum';
import { PersonType } from './models/api/enums/PersonType';
import { SignatureStatusEnum } from '@app/models/api/enums/SignatureStatusEnum';
import { EquityEnum, ResultEnum, RevenueEnum } from '@app/models/api/enums/CapitalSegmentsOptions';

const $t = get(t);

export const getIdentityQuestions = (user: AccountModel): KYCQuestionProps[] => {
	const optionID = [
		{
			id: 1,
			label: getTypeDocumentIdentiteEnumString(TypeDocumentIdentiteEnum.CNI),
			value: TypeDocumentIdentiteEnum.CNI
		},
		{
			id: 2,
			label: getTypeDocumentIdentiteEnumString(TypeDocumentIdentiteEnum.PASSEPORT_UE),
			value: TypeDocumentIdentiteEnum.PASSEPORT_UE
		},
		{
			id: 3,
			label: getTypeDocumentIdentiteEnumString(TypeDocumentIdentiteEnum.PASSEPORT_HORS_UE),
			value: TypeDocumentIdentiteEnum.PASSEPORT_HORS_UE
		}
	];

	return [
		{
			id: 1,
			title: $t('kyc.invest_as.title'),
			order: 1,
			type: KYCQuestionType.SINGLE_CHOICE,
			column: true,
			customerResponse:
				user?.identity?.personType === PersonType.MORALE
					? { responses: { value: 'true' } }
					: user?.identity?.personType === PersonType.PHYSIQUE
						? { responses: { value: 'false' } }
						: {},

			apiKeys: ['identite', 'isPersonneMorale'],
			options: [
				{
					id: 1,
					value: 'false',
					label: $t('kyc.invest_as.options.labelPP')
				},
				{
					id: 2,
					value: 'true',
					label: $t('kyc.invest_as.options.labelPM')
				}
			]
		},

		{
			id: 2,
			title: $t('kyc.id1_document.title'),
			order: 2,
			type: KYCQuestionType.INPUT_FILE,
			readonly: !!user?.documents?.id1?.file && !docIsInvalid(user?.documents?.id1),
			customerResponse:
				user?.documents?.id1 && !docIsInvalid(user?.documents?.id1)
					? {
							response: { value: user?.documents?.id1?.file }
						}
					: {},

			questions: [
				{
					type: KYCQuestionType.INPUT_FILE,
					apiKeys: ['id1'],
					state: (user?.documents?.id1?.status && !docIsInvalid(user?.documents?.id1)) ?? null,
					customerResponse:
						user?.documents?.id1 && !docIsInvalid(user?.documents?.id1)
							? {
									response: { value: user?.documents?.id1?.file }
								}
							: {},
					accept: acceptedFormatsForUserDocument,
					readonly: !!user?.documents?.id1?.file && !docIsInvalid(user?.documents?.id1),
					showPreview: true,
					required: true
				},
				{
					type: KYCQuestionType.INPUT_FILE,
					apiKeys: ['id1Verso'],
					state: (user?.documents?.id1?.status && !docIsInvalid(user?.documents?.id1)) ?? null,
					accept: acceptedFormatsForUserDocument,
					showPreview: true,
					required: true
				},
				{
					id: 1,
					type: KYCQuestionType.INPUT_SELECT,
					apiKeys: ['documents', 'id1'],
					required: true,
					customerResponse:
						user?.documents?.id1 && !docIsInvalid(user?.documents?.id1)
							? {
									response: {
										value: user?.documents?.id1?.documentType
									}
								}
							: {},
					options: optionID
				}
			]
		},
		{
			id: 3,
			title: $t('kyc.dom_document.title'),
			order: 3,
			apiKeys: ['dom'],
			readonly: !!user?.documents?.dom?.file,
			type: KYCQuestionType.INPUT_ADDRESSPROOF,
			state:
				user?.documents?.dom && !docIsInvalid(user?.documents?.dom)
					? user?.documents?.dom?.status
					: null,
			customerResponse:
				user?.documents?.dom && !docIsInvalid(user?.documents?.dom)
					? {
							response: { type: user?.documents?.dom.documentType, file: user?.documents?.dom.file }
						}
					: {},
			questions: [
				{
					id: 15,
					label: $t('kyc.dom_document.questions.thirdPartyCertification.label'),
					type: KYCQuestionType.INPUT_FILE,
					apiKeys: ['documents', 'attestationTiers'],
					state: (user?.documents?.dom?.status && !docIsInvalid(user?.documents?.dom)) ?? null,
					customerResponse: user?.documents?.attestationTiers?.file,
					showPreview: true,
					accept: acceptedFormatsForUserDocument,
					required: false
				},
				{
					id: 16,
					label: $t('kyc.dom_document.questions.thirdPartyID.label'),
					apiKeys: ['documents', 'cniTiers'],
					type: KYCQuestionType.INPUT_FILE,
					accept: acceptedFormatsForUserDocument,
					customerResponse: { response: user?.identity?.cniTiers?.file },
					state: (user?.documents?.dom?.status && !docIsInvalid(user?.documents?.dom)) ?? null,
					showPreview: true,
					required: false
				},
				{
					id: 17,
					apiKeys: ['documents', 'typeAttestDom'],
					type: KYCQuestionType.INPUT_HIDDEN
				}
			]
		},
		{
			id: 4,
			title: $t('kyc.ppe.title'),
			order: 3,
			introduction: $t('kyc.ppe.introduction'),
			type: KYCQuestionType.INPUT_PPE,
			required: true,
			apiKeys: ['informationsPersonnelles', 'ppePersonneConcernee'],
			customerResponse:
				user?.personalInformation?.ppe === undefined
					? ''
					: user?.personalInformation?.ppePersonneConcernee === 'MEMBRE_FAMILLE_OU_PROCHE'
						? 'MEMBRE_FAMILLE_OU_PROCHE'
						: user?.personalInformation?.ppePersonneConcernee === 'MOI_MEME'
							? 'MOI_MEME'
							: 'false',

			options: [
				{ id: 1, label: $t('kyc.ppe.options.selfAffirmation.label'), value: 'MOI_MEME' },
				{
					id: 2,
					label: $t('kyc.ppe.options.CloseAssociate.label'),
					value: 'MEMBRE_FAMILLE_OU_PROCHE'
				},
				{ id: 3, label: $t('kyc.ppe.options.no.label'), value: 'false' }
			],
			questions: [
				{
					id: 11,
					type: KYCQuestionType.INPUT_HIDDEN,
					customerResponse: { response: user?.personalInformation?.ppe?.toString() },
					apiKeys: ['informationsPersonnelles', 'ppe']
				},
				{
					id: 12,
					label: $t('kyc.ppe.questions.ppeFonctionExercee.label'),
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.personalInformation.ppeFonctionExercee },
					apiKeys: ['informationsPersonnelles', 'ppeFonctionExercee'],
					required: false
				},
				{
					id: 7,
					label: $t('kyc.ppe.questions.ppePaysExercice.label'),
					type: KYCQuestionType.INPUT_COUNTRY,
					customerResponse: { response: user?.personalInformation.ppePaysExercice },
					apiKeys: ['informationsPersonnelles', 'ppePaysExercice'],
					required: false
				}
			]
		},

		{
			id: 5,
			title: $t('kyc.identity.title'),
			type: KYCQuestionType.MULTIPLE_QUESTION,
			customerResponse: { response: user?.identity.lastName },
			questions: [
				{
					id: 10,
					label: $t('kyc.identity.questions.civility.label'),
					type: KYCQuestionType.SINGLE_CHOICE,
					column: false,
					apiKeys: ['identite', 'civilite'],
					customerResponse: { responses: { value: user?.identity.civility } },
					options: [
						{
							id: 22,
							value: 'MONSIEUR',
							label: $t('kyc.identity.questions.civility.options.mister.label')
						},
						{
							id: 23,
							value: 'MADAME',
							label: $t('kyc.identity.questions.civility.options.lady.label')
						}
					]
				},
				{
					id: 11,
					label: $t('kyc.identity.questions.lastName.placeholder'),
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.identity.lastName },
					apiKeys: ['identite', 'nomDeNaissance']
				},
				{
					id: 12,
					label: $t('kyc.identity.questions.firstName.placeholder'),
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.identity.firstName },
					apiKeys: ['identite', 'prenom']
				},
				{
					id: 13,
					label: $t('kyc.identity.questions.commonName.placeholder'),
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.identity.commonName },
					apiKeys: ['identite', 'nomDUsage'],
					required: false
				}
			]
		},

		{
			id: 6,
			title: $t('kyc.nationality.title'),
			placeholder: $t('kyc.nationality.placeholder'),
			type: KYCQuestionType.INPUT_COUNTRY,
			customerResponse: { response: user.identity.nationality },
			apiKeys: ['identite', 'nationalite']
		},
		{
			id: 7,
			title: $t('kyc.dateOfBirth.title'),
			type: KYCQuestionType.INPUT_DATE,
			customerResponse: { response: user?.identity?.dateOfBirth },
			apiKeys: ['identite', 'dateNaissance'],
			disabled: false,
			placeholder: formatDateWithLocale(
				new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
				'dd/MM/yyyy'
			),
			options: {
				maxDate: getSubyears(new Date(), 18)
			}
		},
		{
			id: 8,
			title: $t('kyc.placeOfBirth.title'),
			type: KYCQuestionType.INPUT_CITY,
			customerResponse: { response: user?.identity?.birthPlace },
			questions: [
				{
					id: 14,
					placeholder: $t('kyc.placeOfBirth.questions.cityOfBirth.placeholder'),
					label: $t('kyc.placeOfBirth.questions.cityOfBirth.label'),
					customerResponse: { response: user?.identity?.birthPlace },
					type: KYCQuestionType.INPUT_TEXT,
					apiKeys: ['identite', 'lieuNaissance'],
					withClearAction: false,
					required: true
				},
				{
					id: 15,
					placeholder: $t('kyc.placeOfBirth.questions.countyOfBirth.placeholder'),
					label: $t('kyc.placeOfBirth.questions.countyOfBirth.label'),
					type: KYCQuestionType.INPUT_SELECT,
					customerResponse: { response: user?.identity?.countyOfBirth },
					apiKeys: ['identite', 'departementNaissance'],
					withSearchBar: true,
					options: Object.keys(FrenchCounty).map((countyKey) => ({
						label: FrenchCounty[countyKey as keyof typeof FrenchCounty],
						value: countyKey
					})),
					required: true
				},
				{
					id: 16,
					label: $t('kyc.placeOfBirth.questions.countryOfBirth.placeholder'),
					apiKeys: ['identite', 'paysNaissance'],
					type: KYCQuestionType.INPUT_SELECT,
					customerResponse: { response: user?.identity?.countryOfBirth },
					required: true
				},
				{
					id: 17,
					label: $t('kyc.placeOfBirth.questions.postalCodeOfBirth.placeholder'),
					apiKeys: ['identite', 'codePostalNaissance'],
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.identity?.postalCodeOfBirth },
					required: true
				}
			]
		},
		{
			id: 9,
			title: $t('kyc.steps.profile.phoneNumberTitle'),
			introduction: $t('kyc.steps.profile.phoneNumberIntroduction'),
			type: KYCQuestionType.INPUT_TEL,
			customerResponse: { response: user?.coordinates?.phone },
			questions: [
				{
					id: 1,
					type: KYCQuestionType.INPUT_TEL,
					label: $t('kyc.inputs.phone'),
					customerResponse: { response: user?.coordinates?.phone },
					placeholder: $t('kyc.steps.profile.phoneNumberPlaceholder'),
					apiKeys: ['coordonnees', 'telephoneMobile']
				},
				{
					id: 2,
					type: KYCQuestionType.INPUT_SELECT,
					customerResponse: { response: user?.coordinates?.phoneCode },
					apiKeys: ['coordonnees', 'indicatifTelMobile']
				}
			]
		},
		{
			id: 10,
			title: $t('profile.inputs.fiscalAddressTitle'),
			type: KYCQuestionType.INPUT_ADDRESS,
			customerResponse: {
				response:
					user?.coordinates?.fiscalAddress?.address !== null
						? user?.coordinates?.fiscalAddress?.address
						: undefined
			},
			questions: [
				{
					id: 14,
					label: $t('kyc.inputs.fiscalAddress'),
					customerResponse: { response: user?.coordinates?.fiscalAddress?.address },
					type: KYCQuestionType.INPUT_TEXT,
					apiKeys: ['coordonnees', 'adresseFiscale', 'adresse'],
					withClearAction: false
				},
				{
					id: 15,
					label: $t('kyc.inputs.city'),
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.coordinates?.fiscalAddress?.city },
					apiKeys: ['coordonnees', 'adresseFiscale', 'ville']
				},
				{
					id: 16,
					label: $t('kyc.inputs.country'),
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.coordinates?.fiscalAddress?.country },
					apiKeys: ['coordonnees', 'adresseFiscale', 'pays']
				},
				{
					id: 17,
					label: $t('kyc.inputs.postalCode'),
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.coordinates?.fiscalAddress?.postalCode },
					apiKeys: ['coordonnees', 'adresseFiscale', 'codePostal']
				}
			]
		},
		{
			id: 11,
			title: $t('kyc.inputs.professionalStatus.label'),
			type: KYCQuestionType.INPUT_SELECT,
			customerResponse: { response: user?.personalInformation?.situationPro },
			withSearchBar: true,
			apiKeys: ['informationsPersonnelles', 'situationPro'],
			options: [
				{ label: $t('kyc.inputs.professionalStatus.choices.1'), value: 'CHEF_D_ENTREPRISE' },
				{ label: $t('kyc.inputs.professionalStatus.choices.2'), value: 'LIBERALE' },
				{ label: $t('kyc.inputs.professionalStatus.choices.3'), value: 'CADRE' },
				{ label: $t('kyc.inputs.professionalStatus.choices.4'), value: 'EMPLOYE' },
				{ label: $t('kyc.inputs.professionalStatus.choices.5'), value: 'AGRICULTEUR' },
				{ label: $t('kyc.inputs.professionalStatus.choices.6'), value: 'OUVRIER' },
				{ label: $t('kyc.inputs.professionalStatus.choices.7'), value: 'RETRAITE' },
				{ label: $t('kyc.inputs.professionalStatus.choices.8'), value: 'SANS_EMPLOI' }
			]
		},
		{
			id: 12,
			title: $t('kyc.inputs.activity'),
			type: KYCQuestionType.INPUT_SELECT,
			customerResponse: { response: user?.personalInformation?.secteurActivite },
			apiKeys: ['informationsPersonnelles', 'secteurActivite'],
			withSearchBar: true,
			options: Object.keys(ActivitySectorPP).map((key) => ({
				label: ActivitySectorPP[key as keyof typeof ActivitySectorPP],
				value: key
			}))
		},

		{
			id: 13,
			title: $t('kyc.education.title'),
			type: KYCQuestionType.INPUT_SELECT,
			customerResponse: { response: user?.personalInformation?.niveauEducation },
			apiKeys: ['informationsPersonnelles', 'niveauEducation'],
			options: Object.keys(NiveauEducationKYCEnum).map((key) => ({
				label: getNiveauEducationKYCString(key as NiveauEducationKYCEnum),
				value: key
			}))
		},
		{
			id: 14,
			title: $t('kyc.personalInformation.title'),
			type: KYCQuestionType.INPUT_TEXT,
			customerResponse: { response: user?.personalInformation?.profession },
			placeholder: $t('kyc.placeholderProfession'),
			apiKeys: ['informationsPersonnelles', 'profession']
		}
	];
};
export const getCompanyQuestions = (user: AccountModel): KYCQuestionProps[] => {
	return [
		{
			id: 1,
			title: $t('kyc.legalInformationCompany.title'),
			type: KYCQuestionType.INPUT_LEGAL_INFORMATION_COMPANY,
			customerResponse: { response: user?.legalPerson?.registrationCountry },
			questions: [
				{
					id: 10,
					type: KYCQuestionType.INPUT_SELECT,
					apiKeys: ['personneMorale', 'formeJuridique'],
					label: $t('kyc.inputs.legalForm'),
					withSearchBar: true,
					options: Object.keys(LegalFormEnumFR).map((key) => ({
						label: LegalFormEnumFR[key as keyof typeof LegalFormEnumFR],
						value: key
					})),
					required: true,
					customerResponse: { response: user?.legalPerson?.legalForm }
				},
				{
					id: 10,
					type: KYCQuestionType.INPUT_SELECT,
					apiKeys: ['personneMorale', 'representantLegalFonction'],
					label: $t('kyc.inputs.functionOfTheLegalRepresentative.label'),
					options: Object.keys(FunctionOfTheLegalRepresentativeEnumFR).map((key) => ({
						label:
							FunctionOfTheLegalRepresentativeEnumFR[
								key as keyof typeof FunctionOfTheLegalRepresentativeEnumFR
							],
						value: key
					})),

					required: true,
					customerResponse: { response: user?.legalPerson?.functionOfTheLegalRepresentative }
				},
				{
					id: 12,
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.legalPerson?.otherFunctionOfTheLegalRepresentative },
					label: $t('kyc.inputs.otherFunctionOfTheLegalRepresentative'),
					required: false,
					apiKeys: ['personneMorale', 'representantLegalFonctionAutre']
				},
				{
					id: 11,
					type: KYCQuestionType.INPUT_SELECT,
					apiKeys: ['personneMorale', 'immatriculationPays'],
					label: $t('kyc.inputs.registrationCountry'),
					required: true,
					customerResponse: { response: user?.legalPerson?.registrationCountry }
				},
				{
					id: 12,
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.legalPerson?.name },
					label: $t('kyc.inputs.companyName'),
					apiKeys: ['personneMorale', 'denomination']
				},
				{
					id: 13,
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.legalPerson?.registrationNumberSIREN },
					apiKeys: ['personneMorale', 'immatriculationNumero']
				},
				{
					id: 14,
					type: KYCQuestionType.INPUT_TEXT,
					customerResponse: { response: user?.legalPerson?.registrationNumberSIRET },
					label: $t('kyc.inputs.registrationNumberSIRET'),
					apiKeys: ['personneMorale', 'immatriculationSiret']
				},
				{
					id: 15,
					type: KYCQuestionType.INPUT_TEXT,
					label: $t('kyc.inputs.registrationCity'),
					customerResponse: { response: user?.legalPerson?.registrationCity },
					apiKeys: ['personneMorale', 'immatriculationVille']
				},
				{
					id: 16,
					type: KYCQuestionType.INPUT_SELECT,
					label: $t('kyc.inputs.businessActivity'),
					customerResponse: { response: user?.legalPerson?.businessActivity },
					apiKeys: ['personneMorale', 'secteurActivite'],
					withSearchBar: true,
					options: Object.keys(ActivitySectorPM).map((key) => ({
						label: ActivitySectorPM[key as keyof typeof ActivitySectorPM],
						value: key
					}))
				},
				{
					id: 17,
					type: KYCQuestionType.INPUT_TEXT,
					label: $t('kyc.inputs.companyAddress'),
					customerResponse: { response: user?.legalPerson?.fiscalAddress?.address },
					apiKeys: ['personneMorale', 'adresseSiegeSocial', 'adresse']
				},
				{
					id: 18,
					type: KYCQuestionType.INPUT_TEXT,
					label: $t('kyc.inputs.postalCode'),
					customerResponse: { response: user?.legalPerson?.fiscalAddress?.postalCode },
					apiKeys: ['personneMorale', 'adresseSiegeSocial', 'codePostal']
				},
				{
					id: 19,
					type: KYCQuestionType.INPUT_TEXT,
					label: $t('kyc.inputs.city'),
					customerResponse: { response: user?.legalPerson?.fiscalAddress?.city },
					apiKeys: ['personneMorale', 'adresseSiegeSocial', 'ville']
				},
				{
					id: 20,
					type: KYCQuestionType.INPUT_TEXT,
					label: $t('kyc.inputs.country'),
					customerResponse: { response: user?.legalPerson?.fiscalAddress?.country },
					apiKeys: ['personneMorale', 'adresseSiegeSocial', 'pays']
				}
			]
		},
		{
			id: 2,
			title: $t('kyc.beneficiary.questions.isBeneficiary.title'),
			introduction: $t('kyc.beneficiary.questions.isBeneficiary.introduction'),
			type: KYCQuestionType.SINGLE_CHOICE,
			apiKeys: ['personneMorale', 'isBeneficiary'],
			customerResponse:
				isUserBeneficiary(user) || user?.legalPerson?.businessRevenue?.lastClosure
					? {
							responses: {
								value: isUserBeneficiary(user)
									? 'true'
									: user?.legalPerson?.businessRevenue?.lastClosure
										? 'false'
										: null
							}
						}
					: null,
			options: [
				{
					id: 22,
					value: 'true',
					label: $t(
						'kyc.beneficiary.questions.isBeneficiary.options.beneficialOwnerConfirmation.label'
					)
				},
				{
					id: 23,
					value: 'false',
					label: $t('kyc.beneficiary.questions.isBeneficiary.options.no.label')
				}
			]
		},
		{
			id: 3,
			title: $t('kyc.beneficiary.questions.numberOfBeneficiry.title'),
			introduction: $t('kyc.beneficiary.questions.numberOfBeneficiry.introduction'),
			type: KYCQuestionType.SINGLE_CHOICE,
			apiKeys: ['personneMorale', 'associes'],
			customerResponse: isUserBeneficiary(user)
				? user?.legalPerson?.physicalPersonPartners?.length
					? {
							responses: {
								value: (user?.legalPerson?.physicalPersonPartners?.length - 1)?.toString()
							}
						}
					: null
				: user?.legalPerson?.physicalPersonPartners?.length
					? { responses: { value: user?.legalPerson?.physicalPersonPartners?.length?.toString() } }
					: user?.legalPerson?.businessRevenue?.lastClosure
						? '0'
						: null,

			options: [
				{
					id: 22,
					value: '0',
					label: $t('kyc.beneficiary.questions.numberOfBeneficiry.options.nothing.label')
				},
				{
					id: 23,
					value: '1',
					label: $t('kyc.beneficiary.questions.numberOfBeneficiry.options.oneOtherIndividual.label')
				},
				{
					id: 24,
					value: '2',
					label: $t(
						'kyc.beneficiary.questions.numberOfBeneficiry.options.twoOtherIndividuals.label'
					)
				},
				{
					id: 23,
					value: '3',
					label: $t(
						'kyc.beneficiary.questions.numberOfBeneficiry.options.threeOtherIndividuals.label'
					)
				}
			]
		},
		{
			id: 6,
			title: $t('kyc.inputs.businessResults'),
			type: KYCQuestionType.INPUT_SELECT,
			apiKeys: ['personneMorale', 'entrepriseResultats', 'totalBilan'],
			customerResponse: user?.legalPerson?.businessResults?.lastClosure
				? { response: user?.legalPerson?.businessResults.lastClosure }
				: null,
			options: totalBalanceSheetOptions
		},
		{
			id: 7,
			title: $t('kyc.inputs.businessRevenue'),
			type: KYCQuestionType.INPUT_SELECT,
			apiKeys: ['personneMorale', 'entrepriseResultats', 'caNet'],
			customerResponse: user?.legalPerson?.businessRevenue?.lastClosure
				? { response: user?.legalPerson?.businessRevenue.lastClosure }
				: null,
			options: revenueOptions
		},
		{
			id: 8,
			title: $t('kyc.inputs.totalBalanceSheet'),
			type: KYCQuestionType.INPUT_SELECT,
			apiKeys: ['personneMorale', 'entrepriseResultats', 'capitauxPropres'],
			customerResponse: user?.legalPerson?.equity?.lastClosure
				? { response: user?.legalPerson?.equity.lastClosure }
				: null,
			options: equityPMOptions
		},

		{
			id: 8,
			title: $t('kyc.inputs.businessTaxation.label'),
			type: KYCQuestionType.INPUT_SELECT,
			apiKeys: ['personneMorale', 'entrepriseImposition'],
			customerResponse: { response: user?.legalPerson?.businessTaxation },
			description: $t('kyc.inputs.businessTaxation.description'),
			options: [
				{ label: $t('kyc.inputs.businessTaxation.choices.1'), value: 'IS' },
				{ label: $t('kyc.inputs.businessTaxation.choices.2'), value: 'IRPP' }
			]
		},

		{
			id: 4,
			title: $t('kyc.beneficiary.questions.RBERegister.title'),
			introduction: $t('kyc.beneficiary.questions.RBERegister.introduction'),
			type: KYCQuestionType.INPUT_FILE,
			apiKeys: ['documents', 'rbe'],
			customerResponse: {},
			accept: acceptedFormatsForUserDocument,
			readonly: !!user?.documents?.rbe?.file && !docIsInvalid(user?.documents?.rbe),
			showPreview: true,
			required: true
		},

		{
			id: 7,
			title: $t('kyc.beneficiary.questions.KBIS.title'),
			introduction: $t('kyc.beneficiary.questions.KBIS.introduction'),
			type: KYCQuestionType.INPUT_FILE,
			apiKeys: ['documents', 'kbis'],
			customerResponse: {},
			accept: acceptedFormatsForUserDocument,
			readonly: !!user?.documents?.kbis?.file && !docIsInvalid(user?.documents?.kbis),
			showPreview: true,
			required: true
		},
		{
			id: 8,
			title: $t('kyc.beneficiary.questions.companyStatus.title'),
			type: KYCQuestionType.INPUT_FILE,
			apiKeys: ['documents', 'statuts'],
			customerResponse: {},
			accept: acceptedFormatsForUserDocument,
			readonly: !!user?.documents?.statuts?.file && !docIsInvalid(user?.documents?.statuts),
			showPreview: true,
			required: true
		},
		{
			...((user?.legalPerson?.businessResults?.lastClosure !== ResultEnum.NA ||
				user?.legalPerson.businessRevenue?.lastClosure !== RevenueEnum.NA ||
				user?.legalPerson?.equity?.lastClosure !== EquityEnum.NA) && {
				id: 8,
				title: $t('kyc.beneficiary.questions.annualAccounts.title'),
				type: KYCQuestionType.INPUT_FILE,
				apiKeys: ['documents', 'comptesAnnuels'],
				customerResponse: {},
				accept: acceptedFormatsForUserDocument,
				readonly:
					!!user?.documents?.annualAccounts?.file && !docIsInvalid(user?.documents?.annualAccounts),
				showPreview: true,
				required: true
			})
		}
	];
};
export const getSimulatorQuestions = (user: AccountModel): KYCQuestionProps[] => {
	return [
		{
			id: 1,
			title: $t('kyc.steps.simulator.annualNetIncome.label'),
			introduction: $t('kyc.steps.simulator.annualNetIncome.info'),
			type: KYCQuestionType.INPUT_NUMBER,
			apiKeys: ['donneesFinancieres', 'montantRevenusAnnuels'],
			placeholder: $t('kyc.steps.simulator.annualNetIncome.placeholder'),
			customerResponse: { response: user?.financialData?.annualNetIncome }
		},
		{
			id: 2,
			title: $t('kyc.steps.simulator.liquidAssetAmount.label'),
			introduction: $t('kyc.steps.simulator.liquidAssetAmount.info'),
			type: KYCQuestionType.INPUT_NUMBER,
			apiKeys: ['donneesFinancieres', 'montantActifsLiquides'],
			placeholder: $t('kyc.steps.simulator.liquidAssetAmount.placeholder'),
			customerResponse: { response: user?.financialData?.liquidAssetAmount }
		},
		{
			id: 3,
			title: $t('kyc.steps.simulator.annualFinancialCommitmentAmount.label'),
			introduction: $t('kyc.steps.simulator.annualFinancialCommitmentAmount.info'),
			type: KYCQuestionType.INPUT_NUMBER,
			placeholder: $t('kyc.steps.simulator.annualFinancialCommitmentAmount.placeholder'),
			apiKeys: ['donneesFinancieres', 'montantEngagementsFinanciers'],
			customerResponse: { response: user?.financialData?.annualFinancialCommitmentAmount }
		},
		{
			id: 4,
			type: KYCQuestionType.MULTIPLE_CHOICE,
			apiKeys: ['donneesFinancieres', 'recuResultatsSimulation'],
			value: user?.financialData?.receivedSimulationResult ? ['yes'] : undefined,
			customerResponse: {
				responses: user?.financialData?.receivedSimulationResult ? ['yes'] : undefined
			},
			column: true,
			options: [
				{
					id: 1,
					value: 'yes',
					label: $t('kyc.steps.simulator.result.label')
				}
			]
		},
		{
			id: 5,
			title: $t('kyc.steps.simulator.share.label'),
			introduction: $t('kyc.steps.simulator.share.info'),
			type: KYCQuestionType.SINGLE_CHOICE,
			apiKeys: ['donneesFinancieres', 'partageDonneesFinancieres'],
			column: true,
			customerResponse: {
				responses: {
					value:
						user?.financialData?.financialDataSharing === true
							? 'yes'
							: user?.financialData?.financialDataSharing === false
								? 'no'
								: ''
				}
			},

			options: [
				{
					id: 1,
					value: 'yes',
					label: $t('yes')
				},
				{
					id: 2,
					value: 'no',
					label: $t('no')
				}
			]
		}
	];
};

export const getSignatureQuestions = (user: AccountModel): KYCQuestionProps[] => {
	return [
		{
			id: 1,
			title: $t('kyc.steps.signature.phoneNumberTitle'),
			introduction: $t('kyc.steps.signature.phoneNumberIntroduction'),
			type: KYCQuestionType.INPUT_TEL,
			customerResponse:
				user?.signatureIdentityConfirmation?.status === SignatureStatusEnum.STATUT_OK,
			questions: [
				{
					id: 1,
					type: KYCQuestionType.INPUT_TEL,
					label: $t('kyc.inputs.phone'),
					customerResponse: { response: user?.coordinates?.phone },
					placeholder: $t('kyc.steps.signature.phoneNumberPlaceholder'),
					apiKeys: ['signature', 'telephoneMobile']
				},
				{
					id: 2,
					type: KYCQuestionType.INPUT_SELECT,
					customerResponse: { response: user?.coordinates?.phoneCode },
					apiKeys: ['signature', 'indicatifTelMobile']
				}
			]
		}
	];
};

export const getBeneficiaryQuestion = (
	beneficiary?: PhysicalPersonPartner,
	disabledOption = false
): KYCQuestionProps[] => {
	return [
		{
			id: 5,
			title: $t('kyc.steps.legalPerson.partnerInformationsTitle'),
			type: KYCQuestionType.INPUT_FILE,
			apiKeys: ['documents', 'cni'],
			readonly: !!beneficiary?.cniRecto,
			accept: acceptedFormatsForUserDocument,
			showPreview: true
		},
		{
			id: 6,
			title: $t('kyc.steps.legalPerson.partnerDocumentsTitle'),
			introduction: $t('kyc.steps.legalPerson.introduction'),
			type: KYCQuestionType.MULTIPLE_QUESTION,
			apiKeys: ['personneMorale', 'personnesPhysiquesAssociees'],
			questions: [
				{
					id: 10,
					label: $t('kyc.identity.questions.civility.label'),
					type: KYCQuestionType.SINGLE_CHOICE,
					customerResponse: { responses: { value: beneficiary?.civility } },
					column: false,
					apiKeys: ['informations', 'civilite'],
					disabled: disabledOption,
					options: [
						{
							id: 22,
							value: 'MONSIEUR',
							label: $t('kyc.inputs.civility.choices.1')
						},
						{
							id: 23,
							value: 'MADAME',
							label: $t('kyc.inputs.civility.choices.2')
						}
					]
				},
				{
					id: 11,
					label: $t('kyc.identity.questions.lastName.placeholder'),
					apiKeys: ['informations', 'nom'],
					customerResponse: { response: beneficiary?.lastName },
					disabled: disabledOption,
					type: KYCQuestionType.INPUT_TEXT
				},
				{
					id: 12,
					label: $t('kyc.identity.questions.firstName.placeholder'),
					apiKeys: ['informations', 'prenom'],
					type: KYCQuestionType.INPUT_TEXT,
					disabled: disabledOption,
					customerResponse: { response: beneficiary?.firstName }
				},
				{
					id: 13,
					label: $t('kyc.inputs.country'),
					apiKeys: ['informations', 'pays'],
					type: KYCQuestionType.INPUT_COUNTRY,
					disabled: disabledOption,
					customerResponse: { response: beneficiary?.country }
				},
				{
					id: 14,
					label: $t('profile.inputs.nationality'),
					apiKeys: ['informations', 'nationalite'],
					type: KYCQuestionType.INPUT_COUNTRY,
					disabled: disabledOption,
					customerResponse: { response: beneficiary?.nationality }
				},
				{
					id: 15,
					apiKeys: ['informations', 'id'],
					type: KYCQuestionType.INPUT_HIDDEN,
					customerResponse: { response: beneficiary?.id }
				}
			]
		}
	];
};

export const totalBalanceSheetOptions = [
	{ label: 'Non Applicable (1er exercice)', value: String(ResultEnum.NA), id: 1 },
	{ label: 'Moins de 1 000 000 €', value: String(ResultEnum.MOINS_1M), id: 2 },
	{ label: 'De 1 000 001 € à 10 000 000 €', value: String(ResultEnum.DE_1M_A_10M), id: 3 },
	{ label: 'De 10 000 001 € à 20 000 000 €', value: String(ResultEnum.DE_10M_A_20M), id: 4 },
	{ label: 'Plus de 20 000 000 €', value: String(ResultEnum.PLUS_20M), id: 5 }
];
export const revenueOptions = [
	{ label: 'Non Applicable (1er exercice)', value: String(RevenueEnum.NA), id: 1 },
	{ label: 'Moins de 2 000 000 €', value: String(RevenueEnum.MOINS_2M), id: 2 },
	{ label: 'De 2 000 001 € à 10 000 000 €', value: String(RevenueEnum.DE_2M_A_10M), id: 3 },
	{ label: 'De 10 000 001 € à 20 000 000 €', value: String(RevenueEnum.DE_10M_A_20M), id: 4 },
	{ label: 'De 20 000 001 € à 30 000 000 €', value: String(RevenueEnum.DE_20M_A_30M), id: 5 },
	{ label: 'De 30 000 001 € à 40 000 000 €', value: String(RevenueEnum.DE_30M_A_40M), id: 6 },
	{ label: 'Plus de 40 000 000 €', value: String(RevenueEnum.PLUS_40M), id: 7 }
];

export const equityPMOptions = [
	{ label: 'Non Applicable (1er exercice)', value: String(EquityEnum.NA), id: 1 },
	{ label: 'Moins de 100 000 €', value: String(EquityEnum.MOINS_100K), id: 2 },
	{ label: 'De 100 001 € à 1 000 000 €', value: String(EquityEnum.DE_100K_A_1M), id: 3 },
	{ label: 'De 1 000 001 € à 2 000 000 €', value: String(EquityEnum.DE_1M_A_2M), id: 4 },
	{ label: 'Plus de 2 000 000 €', value: String(EquityEnum.PLUS_2M), id: 5 }
];
